import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Pencil, Trash2, X, TrendingUp, TrendingDown, Star, Repeat, ChevronLeft, PiggyBank, GraduationCap, Settings2, Percent } from 'lucide-react'
import {
  formatCents,
  parseEuroToCents,
  type AllowanceAccountDto,
  type AllowanceTxnDto,
  type AllowanceRuleDto,
  type SavingsGoalDto,
  type MemberDto,
  type AllowanceTxnType,
  type GradeRewardDto,
  type GradeRewardKind,
  type BankConfigDto,
} from '@familynest/shared'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../store/auth'
import { useLive } from '../../lib/ws'
import { Avatar, Button, useBackdropClose } from '../ui'
import { useDialogs } from '../ui/dialogs'

const field = 'rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)] w-full'
const fieldStyle = { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' } as const
const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']

export function AllowanceScreen({ members }: { members: MemberDto[] }): React.JSX.Element {
  const { member: me } = useAuth()
  const memberMap = useMemo(() => new Map(members.map((m) => [m.id, m])), [members])
  const [accounts, setAccounts] = useState<AllowanceAccountDto[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const load = useCallback(async () => setAccounts(await api.listAccounts()), [])
  useEffect(() => {
    void load()
  }, [load])
  useLive('allowance', load)

  const isChild = me?.role === 'child'
  const selected = accounts.find((a) => a.id === selectedId) ?? null
  const childrenWithoutAccount = members.filter((m) => m.role === 'child' && !accounts.some((a) => a.memberId === m.id))

  // ADR-048: Genau ein sichtbares Konto → direkt hineinspringen (die Übersicht mit
  // nur einem Eintrag wird übersprungen). Für Kinder immer der Fall; für Eltern mit
  // nur einem Kind ebenso. Nur EINMAL automatisch, damit Eltern per „Zurück" wieder
  // zur Übersicht kommen (z. B. um für weitere Kinder Konten anzulegen).
  const autoSelected = useRef(false)
  useEffect(() => {
    if (!autoSelected.current && !selectedId && accounts.length === 1) {
      autoSelected.current = true
      setSelectedId(accounts[0]!.id)
    }
  }, [selectedId, accounts])

  async function addAccount(memberId: string): Promise<void> {
    const acc = await api.createAccount(memberId)
    await load()
    setSelectedId(acc.id)
  }

  // Kind: keine Konten-Übersicht (nur das eigene Konto, kein „Zurück").
  if (isChild) {
    return (
      <div className="h-full overflow-auto">
        {selected && me ? (
          <AccountDetail key={selected.id} account={selected} me={me} members={memberMap} onChanged={load} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
            <PiggyBank className="size-8" /> Noch kein Taschengeld-Konto angelegt.
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="grid h-full grid-cols-1 md:grid-cols-[20rem_1fr]">
      <aside className={`flex-col border-r ${selected ? 'hidden md:flex' : 'flex'}`} style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-card)' }}>
        <div className="px-4 py-3">
          <h1 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Taschengeld</h1>
        </div>
        <div className="flex-1 overflow-auto px-2 pb-2">
          {accounts.map((a) => {
            const m = memberMap.get(a.memberId)
            return (
              <button key={a.id} onClick={() => setSelectedId(a.id)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-black/5" style={a.id === selectedId ? { backgroundColor: 'var(--color-accent)' + '18' } : undefined}>
                {m && <Avatar name={m.name} color={m.color} size={36} src={m.avatarUrl} />}
                <span className="min-w-0 flex-1">
                  <span className="block font-medium" style={{ color: 'var(--color-ink)' }}>{m?.name ?? 'Kind'}</span>
                  <span className="text-sm" style={{ color: a.balanceCents < 0 ? 'var(--color-danger)' : 'var(--color-muted)' }}>{formatCents(a.balanceCents)}</span>
                </span>
              </button>
            )
          })}
          {accounts.length === 0 && <p className="px-3 py-6 text-center text-sm" style={{ color: 'var(--color-muted)' }}>Noch keine Konten.</p>}
          {me?.role === 'adult' && childrenWithoutAccount.map((c) => (
            <button key={c.id} onClick={() => addAccount(c.id)} className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-black/5" style={{ color: 'var(--color-accent)' }}>
              <Plus className="size-4" /> Konto für {c.name} anlegen
            </button>
          ))}
        </div>
      </aside>

      <section className={`min-h-0 ${selected ? 'flex' : 'hidden md:flex'} flex-col overflow-auto`}>
        {selected && me ? (
          <AccountDetail key={selected.id} account={selected} me={me} members={memberMap} onBack={() => setSelectedId(null)} onChanged={load} />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
            <PiggyBank className="size-8" /> Wähle ein Konto.
          </div>
        )}
      </section>
    </div>
  )
}

const TXN_META: Record<AllowanceTxnType, { label: string; icon: typeof Star }> = {
  credit_auto: { label: 'Taschengeld', icon: Repeat },
  credit_manual: { label: 'Gutschrift', icon: TrendingUp },
  bonus: { label: 'Bonus', icon: Star },
  expense: { label: 'Ausgabe', icon: TrendingDown },
  correction: { label: 'Korrektur', icon: Pencil },
  interest_credit: { label: 'Zinsen', icon: Percent },
  interest_debit: { label: 'Dispozinsen', icon: Percent },
}

function AccountDetail({ account, me, members, onBack, onChanged }: { account: AllowanceAccountDto; me: MemberDto; members: Map<string, MemberDto>; onBack?: () => void; onChanged: () => void }): React.JSX.Element {
  const [txns, setTxns] = useState<AllowanceTxnDto[]>([])
  const [rule, setRule] = useState<AllowanceRuleDto | null>(null)
  const [goals, setGoals] = useState<SavingsGoalDto[]>([])
  const [book, setBook] = useState<AllowanceTxnType | null>(null)
  const [ruleOpen, setRuleOpen] = useState(false)
  const [goalOpen, setGoalOpen] = useState(false)
  const [gradeOpen, setGradeOpen] = useState(false)
  const [overdraftOpen, setOverdraftOpen] = useState(false)
  const [bank, setBank] = useState<BankConfigDto | null>(null)
  const [bankOpen, setBankOpen] = useState(false)

  const isAdult = me.role === 'adult'
  const canGoals = isAdult || account.memberId === me.id
  const child = members.get(account.memberId)

  const load = useCallback(async () => {
    // allSettled statt all: schlägt ein Teil-Request fehl (labiler WebView nach
    // Hintergrund), bleiben die übrigen Werte trotzdem aktuell und der Saldo-Refresh
    // (onChanged) läuft in jedem Fall — sonst friert die Anzeige nach einer Buchung ein.
    const [t, r, g, b] = await Promise.allSettled([api.listTransactions(account.id), api.getRule(account.id), api.listGoals(account.id), api.getBankConfig()])
    if (t.status === 'fulfilled') setTxns(t.value)
    if (r.status === 'fulfilled') setRule(r.value)
    if (g.status === 'fulfilled') setGoals(g.value)
    if (b.status === 'fulfilled') setBank(b.value)
    onChanged()
  }, [account.id, onChanged])
  useEffect(() => {
    void load()
  }, [load])
  useLive('allowance', load)

  const free = account.balanceCents - account.reservedCents
  const overdraft = account.overdraftLimitCents
  const availableToSpend = account.balanceCents + overdraft

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
      {/* Kopf mit Saldo */}
      <div className="flex items-center gap-2">
        {onBack && <button onClick={onBack} className="rounded-lg p-1 hover:bg-black/5 md:hidden" aria-label="Zurück"><ChevronLeft className="size-5" style={{ color: 'var(--color-ink)' }} /></button>}
        {child && <Avatar name={child.name} color={child.color} size={32} src={child.avatarUrl} />}
        <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>{child?.name ?? 'Kind'}</h2>
      </div>

      <div className="rounded-[var(--radius-card)] border p-5 text-center" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
        <div className="text-sm" style={{ color: 'var(--color-muted)' }}>Kontostand</div>
        <div className="text-4xl font-bold" style={{ color: account.balanceCents < 0 ? 'var(--color-danger)' : 'var(--color-ink)' }}>{formatCents(account.balanceCents)}</div>
        {account.reservedCents > 0 && <div className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>davon {formatCents(account.reservedCents)} reserviert · frei {formatCents(free)}</div>}
        {overdraft > 0 && (
          <div className="mt-1 text-xs" style={{ color: account.balanceCents < 0 ? 'var(--color-danger)' : 'var(--color-muted)' }}>
            {account.balanceCents < 0
              ? `Dispo genutzt: ${formatCents(Math.min(-account.balanceCents, overdraft))} von ${formatCents(overdraft)}`
              : `verfügbar inkl. Dispo: ${formatCents(availableToSpend)}`}
          </div>
        )}
        {isAdult && (
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button onClick={() => setBook('credit_manual')}><TrendingUp className="size-4" /> Gutschrift</Button>
            <Button variant="ghost" onClick={() => setBook('expense')}><TrendingDown className="size-4" /> Ausgabe</Button>
            <Button variant="ghost" onClick={() => setBook('correction')}><Pencil className="size-4" /> Korrektur</Button>
            <Button variant="ghost" onClick={() => setGradeOpen(true)}><GraduationCap className="size-4" /> Note</Button>
          </div>
        )}
      </div>

      {/* Regel */}
      <div className="rounded-[var(--radius-card)] border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>Wiederkehrende Gutschrift</h3>
          {isAdult && <button onClick={() => setRuleOpen(true)} className="rounded p-1 hover:bg-black/5" aria-label="Regel bearbeiten"><Pencil className="size-4" style={{ color: 'var(--color-muted)' }} /></button>}
        </div>
        {rule ? (
          <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
            {formatCents(rule.amountCents)} {rule.interval === 'weekly' ? `wöchentlich (${WEEKDAYS[rule.dayOfWeek ?? 1]})` : `monatlich (am ${rule.dayOfMonth}.)`} · nächste am {new Date(rule.nextRunAt).toLocaleDateString('de-DE')}
            {!rule.active && ' · pausiert'}
          </p>
        ) : (
          <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>Keine Regel eingerichtet.</p>
        )}
      </div>

      {/* Dispo-Rahmen (pro Kind, Eltern pflegen) */}
      {(isAdult || overdraft > 0) && (
        <div className="rounded-[var(--radius-card)] border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>Dispo-Rahmen</h3>
            {isAdult && <button onClick={() => setOverdraftOpen(true)} className="rounded p-1 hover:bg-black/5" aria-label="Dispo bearbeiten"><Pencil className="size-4" style={{ color: 'var(--color-muted)' }} /></button>}
          </div>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
            {overdraft > 0 ? `Konto darf bis ${formatCents(overdraft)} ins Minus (wie ein Dispo bei der Bank).` : 'Kein Dispo — das Konto darf nicht ins Minus.'}
          </p>
        </div>
      )}

      {/* Bank-Konditionen (Zinsen, familienweit; Eltern pflegen) */}
      {bank && (bank.interestActive || isAdult) && (
        <div className="rounded-[var(--radius-card)] border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>Bank-Konditionen</h3>
            {isAdult && <button onClick={() => setBankOpen(true)} className="rounded p-1 hover:bg-black/5" aria-label="Zinsen bearbeiten"><Pencil className="size-4" style={{ color: 'var(--color-muted)' }} /></button>}
          </div>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
            {bank.interestActive
              ? `+${(bank.creditRateBpsMonthly / 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} % Zinsen/Monat aufs Guthaben · −${(bank.debitRateBpsMonthly / 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} % im Minus (Dispozinsen)`
              : 'Zinsen sind ausgeschaltet.'}
          </p>
        </div>
      )}

      {/* Sparziele */}
      <div className="rounded-[var(--radius-card)] border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>Sparziele</h3>
          {canGoals && <Button variant="ghost" onClick={() => setGoalOpen(true)}><Plus className="size-4" /> Neu</Button>}
        </div>
        <div className="mt-2 flex flex-col gap-3">
          {goals.length === 0 && <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Noch keine Sparziele.</p>}
          {goals.map((g) => (
            <GoalRow key={g.id} goal={g} canManage={canGoals} onChanged={load} />
          ))}
        </div>
      </div>

      {/* Verlauf */}
      <div className="rounded-[var(--radius-card)] border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
        <h3 className="mb-2 text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>Verlauf</h3>
        {txns.length === 0 && <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Noch keine Buchungen.</p>}
        <ul className="flex flex-col">
          {txns.map((t) => {
            const meta = TXN_META[t.type]
            const Icon = meta.icon
            const pos = t.amountCents >= 0
            const booker = t.createdBy ? (members.get(t.createdBy)?.name ?? 'jemand') : t.type === 'credit_auto' ? 'automatisch' : 'System'
            return (
              <li key={t.id} className="flex items-center gap-3 border-b py-2 last:border-0" style={{ borderColor: 'var(--color-line)' }}>
                <Icon className="size-4 shrink-0" style={{ color: pos ? 'var(--color-success)' : 'var(--color-danger)' }} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm" style={{ color: 'var(--color-ink)' }}>{meta.label}{t.note ? ` · ${t.note}` : ''}</span>
                  <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                    {new Date(t.createdAt).toLocaleDateString('de-DE')} · {booker === 'automatisch' ? 'automatisch' : `von ${booker}`}
                  </span>
                </span>
                <span className="font-medium" style={{ color: pos ? 'var(--color-success)' : 'var(--color-danger)' }}>{pos ? '+' : ''}{formatCents(t.amountCents)}</span>
              </li>
            )
          })}
        </ul>
      </div>

      {book && <BookDialog accountId={account.id} type={book} onClose={() => setBook(null)} onSaved={() => { setBook(null); void load() }} />}
      {overdraftOpen && <OverdraftDialog accountId={account.id} current={overdraft} ruleAmountCents={rule?.amountCents ?? null} onClose={() => setOverdraftOpen(false)} onSaved={() => { setOverdraftOpen(false); void load() }} />}
      {bankOpen && bank && <BankConfigDialog current={bank} onClose={() => setBankOpen(false)} onSaved={() => { setBankOpen(false); void load() }} />}
      {ruleOpen && <RuleDialog accountId={account.id} rule={rule} onClose={() => setRuleOpen(false)} onSaved={() => { setRuleOpen(false); void load() }} />}
      {goalOpen && <GoalDialog accountId={account.id} onClose={() => setGoalOpen(false)} onSaved={() => { setGoalOpen(false); void load() }} />}
      {gradeOpen && <GradeBonusDialog accountId={account.id} childName={child?.name ?? 'Kind'} onClose={() => setGradeOpen(false)} onSaved={() => { setGradeOpen(false); void load() }} />}
    </div>
  )
}

function GoalRow({ goal, canManage, onChanged }: { goal: SavingsGoalDto; canManage: boolean; onChanged: () => void }): React.JSX.Element {
  const dialogs = useDialogs()
  const pct = Math.min(100, Math.round((goal.reservedCents / goal.targetCents) * 100))
  const done = goal.achievedAt !== null
  async function reserve(sign: 1 | -1): Promise<void> {
    const v = await dialogs.prompt({ title: sign > 0 ? 'Zurücklegen' : 'Zurückholen', message: sign > 0 ? 'Wieviel zurücklegen? (€)' : 'Wieviel zurückholen? (€)', placeholder: 'z. B. 5,00' })
    if (!v) return
    let cents: number
    try {
      cents = parseEuroToCents(v)
    } catch {
      await dialogs.alert({ title: 'Ungültiger Betrag', message: 'Bitte einen Betrag wie 5,00 eingeben.' })
      return
    }
    try {
      await api.updateGoal(goal.id, { reserveDeltaCents: sign * cents })
      onChanged()
    } catch (e) {
      // Deckungsprüfung (Backend): mehr zurücklegen als gedeckt → klare Meldung.
      const insufficient = e instanceof ApiError && e.status === 400
      await dialogs.alert({ title: insufficient ? 'Nicht genug Guthaben' : 'Fehlgeschlagen', message: insufficient ? 'Du kannst nicht mehr zurücklegen als auf dem Konto ist.' : 'Bitte später erneut versuchen.' })
    }
  }
  async function remove(): Promise<void> {
    if (await dialogs.confirm({ title: 'Sparziel löschen', message: `Sparziel „${goal.title}" löschen?`, confirmLabel: 'Löschen', danger: true })) {
      await api.deleteGoal(goal.id)
      onChanged()
    }
  }
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span style={{ color: 'var(--color-ink)' }}>{done ? '🎉 ' : ''}{goal.title}</span>
        <span style={{ color: 'var(--color-muted)' }}>{formatCents(goal.reservedCents)} / {formatCents(goal.targetCents)}</span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full" style={{ backgroundColor: 'var(--color-surface)' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: done ? 'var(--color-success)' : 'var(--color-accent)' }} />
      </div>
      {canManage && (
        <div className="mt-1 flex gap-2">
          <button onClick={() => reserve(1)} className="text-xs" style={{ color: 'var(--color-accent)' }}>+ zurücklegen</button>
          <button onClick={() => reserve(-1)} className="text-xs" style={{ color: 'var(--color-muted)' }}>− zurückholen</button>
          <button onClick={remove} className="ml-auto text-xs" style={{ color: 'var(--color-muted)' }}>löschen</button>
        </div>
      )}
    </div>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }): React.JSX.Element {
  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="max-h-[90vh] w-full max-w-sm overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>{title}</h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}

function BookDialog({ accountId, type, onClose, onSaved }: { accountId: string; type: AllowanceTxnType; onClose: () => void; onSaved: () => void }): React.JSX.Element {
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [negative, setNegative] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const title = type === 'expense' ? 'Ausgabe buchen' : type === 'credit_manual' ? 'Gutschrift buchen' : 'Korrektur buchen'

  async function save(): Promise<void> {
    // Betrag zuerst (lokal) prüfen — so wird ein späterer Netzwerkfehler NICHT
    // fälschlich als „ungültiger Betrag" angezeigt (führte auf dem Mac dazu, dass
    // eine in Wahrheit gescheiterte Buchung wie ein Tippfehler aussah).
    let cents: number
    try {
      cents = parseEuroToCents(amount)
    } catch {
      setError('Bitte gültigen Betrag eingeben.')
      return
    }
    if (type === 'correction' && negative) cents = -cents
    setBusy(true)
    setError(null)
    try {
      await api.bookTransaction(accountId, { amountCents: cents, type: type as 'expense' | 'credit_manual' | 'correction', note: note.trim() || undefined })
      onSaved()
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 422
          ? 'Dispo-Limit erreicht — diese Ausgabe würde das Konto zu weit ins Minus bringen. Zum Überschreiten als „Korrektur" buchen.'
          : err instanceof ApiError
            ? `Buchung fehlgeschlagen (Fehler ${err.status}). Bitte erneut versuchen.`
            : 'Keine Verbindung zum Server — die Buchung wurde NICHT gespeichert. Bitte Verbindung prüfen und erneut versuchen.',
      )
      setBusy(false)
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--color-muted)' }}>Betrag (€)</span>
          <input className={field} style={fieldStyle} inputMode="decimal" placeholder="z. B. 5,00" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </label>
        {type === 'correction' && (
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-ink)' }}>
            <input type="checkbox" checked={negative} onChange={(e) => setNegative(e.target.checked)} /> Abzug (Minus)
          </label>
        )}
        <input className={field} style={fieldStyle} placeholder="Notiz (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        <div className="mt-1 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={save} disabled={busy || !amount.trim()}>Buchen</Button>
        </div>
      </div>
    </Modal>
  )
}

function OverdraftDialog({ accountId, current, ruleAmountCents, onClose, onSaved }: { accountId: string; current: number; ruleAmountCents: number | null; onClose: () => void; onSaved: () => void }): React.JSX.Element {
  const [amount, setAmount] = useState((current / 100).toFixed(2).replace('.', ','))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const suggestion = ruleAmountCents ? ruleAmountCents * 3 : null

  async function save(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const cents = amount.trim() ? parseEuroToCents(amount) : 0
      await api.setOverdraft(accountId, cents)
      onSaved()
    } catch {
      setError('Bitte gültigen Betrag (≥ 0) eingeben.')
      setBusy(false)
    }
  }

  return (
    <Modal title="Dispo-Rahmen" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Wie weit darf das Konto ins Minus? 0 € = kein Dispo.</p>
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--color-muted)' }}>Rahmen (€)</span>
          <input className={field} style={fieldStyle} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </label>
        {suggestion !== null && (
          <button type="button" onClick={() => setAmount((suggestion / 100).toFixed(2).replace('.', ','))} className="self-start text-xs" style={{ color: 'var(--color-accent)' }}>
            Vorschlag: 3× Taschengeld = {formatCents(suggestion)}
          </button>
        )}
        {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        <div className="mt-1 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={save} disabled={busy}>Speichern</Button>
        </div>
      </div>
    </Modal>
  )
}

function BankConfigDialog({ current, onClose, onSaved }: { current: BankConfigDto; onClose: () => void; onSaved: () => void }): React.JSX.Element {
  const [credit, setCredit] = useState((current.creditRateBpsMonthly / 100).toString().replace('.', ','))
  const [debit, setDebit] = useState((current.debitRateBpsMonthly / 100).toString().replace('.', ','))
  const [active, setActive] = useState(current.interestActive)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toBps = (s: string): number => Math.round(Number(s.replace(',', '.')) * 100)

  async function save(): Promise<void> {
    const c = toBps(credit)
    const d = toBps(debit)
    if (!Number.isFinite(c) || !Number.isFinite(d) || c < 0 || d < 0 || c > 5000 || d > 5000) {
      setError('Sätze zwischen 0 und 50 % eingeben.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.updateBankConfig({ creditRateBpsMonthly: c, debitRateBpsMonthly: d, interestActive: active })
      onSaved()
    } catch {
      setError('Speichern fehlgeschlagen.')
      setBusy(false)
    }
  }

  return (
    <Modal title="Bank-Konditionen" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Zinssätze pro Monat — gelten für alle Kinder.</p>
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--color-muted)' }}>Habenzins (Guthaben) — % pro Monat</span>
          <input className={field} style={fieldStyle} inputMode="decimal" value={credit} onChange={(e) => setCredit(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--color-muted)' }}>Sollzins (im Minus) — % pro Monat</span>
          <input className={field} style={fieldStyle} inputMode="decimal" value={debit} onChange={(e) => setDebit(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-ink)' }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Zinsen aktiv
        </label>
        {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        <div className="mt-1 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={save} disabled={busy}>Speichern</Button>
        </div>
      </div>
    </Modal>
  )
}

function RuleDialog({ accountId, rule, onClose, onSaved }: { accountId: string; rule: AllowanceRuleDto | null; onClose: () => void; onSaved: () => void }): React.JSX.Element {
  const [amount, setAmount] = useState(rule ? (rule.amountCents / 100).toString().replace('.', ',') : '')
  const [interval, setInterval] = useState<'weekly' | 'monthly'>(rule?.interval ?? 'weekly')
  const [dayOfWeek, setDayOfWeek] = useState(rule?.dayOfWeek ?? 1)
  const [dayOfMonth, setDayOfMonth] = useState(rule?.dayOfMonth ?? 1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const cents = parseEuroToCents(amount)
      await api.upsertRule(accountId, { amountCents: cents, interval, dayOfWeek: interval === 'weekly' ? dayOfWeek : null, dayOfMonth: interval === 'monthly' ? dayOfMonth : null })
      onSaved()
    } catch {
      setError('Bitte gültigen Betrag eingeben.')
      setBusy(false)
    }
  }
  async function removeRule(): Promise<void> {
    setBusy(true)
    await api.deleteRule(accountId)
    onSaved()
  }

  return (
    <Modal title="Wiederkehrende Gutschrift" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--color-muted)' }}>Betrag (€)</span>
          <input className={field} style={fieldStyle} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </label>
        <div className="flex gap-2">
          {(['weekly', 'monthly'] as const).map((iv) => (
            <button key={iv} type="button" onClick={() => setInterval(iv)} className="flex-1 rounded-lg px-3 py-2 text-sm" style={interval === iv ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { backgroundColor: 'var(--color-surface)', color: 'var(--color-ink)', border: '1px solid var(--color-line)' }}>
              {iv === 'weekly' ? 'Wöchentlich' : 'Monatlich'}
            </button>
          ))}
        </div>
        {interval === 'weekly' ? (
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Wochentag</span>
            <select className={field} style={fieldStyle} value={dayOfWeek} onChange={(e) => setDayOfWeek(Number(e.target.value))}>
              {[1, 2, 3, 4, 5, 6, 0].map((d) => <option key={d} value={d}>{WEEKDAYS[d]}</option>)}
            </select>
          </label>
        ) : (
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Tag im Monat</span>
            <input type="number" min={1} max={31} className={field} style={fieldStyle} value={dayOfMonth} onChange={(e) => setDayOfMonth(Math.min(31, Math.max(1, Number(e.target.value))))} />
          </label>
        )}
        {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        <div className="mt-1 flex items-center justify-between">
          {rule ? <Button variant="danger" onClick={removeRule} disabled={busy}><Trash2 className="size-4" /> Löschen</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
            <Button onClick={save} disabled={busy || !amount.trim()}>Speichern</Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

const KIND_LABEL: Record<GradeRewardKind, string> = { report: 'Zeugnis', exam: 'Arbeit/HÜ' }
/** Anzeige-Reihenfolge: Arbeit/HÜ zuerst (häufigster Fall) und Vorauswahl im Dialog. */
const KIND_ORDER: readonly GradeRewardKind[] = ['exam', 'report']

function GradeBonusDialog({ accountId, childName, onClose, onSaved }: { accountId: string; childName: string; onClose: () => void; onSaved: () => void }): React.JSX.Element {
  const [rewards, setRewards] = useState<GradeRewardDto[]>([])
  const [grade, setGrade] = useState(1)
  const [kind, setKind] = useState<GradeRewardKind>(KIND_ORDER[0]!)
  const [mode, setMode] = useState<'pick' | 'edit'>('pick')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadRewards = useCallback(async () => setRewards(await api.listGradeRewards()), [])
  useEffect(() => { void loadRewards() }, [loadRewards])

  const grades = useMemo(() => {
    const s = new Set(rewards.map((r) => r.grade))
    const arr = [...s].sort((a, b) => a - b)
    return arr.length ? arr : [1, 2, 3]
  }, [rewards])
  const amount = rewards.find((r) => r.grade === grade && r.kind === kind)?.amountCents ?? 0

  async function book(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await api.bookGradeBonus(accountId, { grade, kind })
      onSaved()
    } catch {
      setError('Buchen fehlgeschlagen (kein Betrag für diese Note?).')
      setBusy(false)
    }
  }

  if (mode === 'edit') {
    return <GradeRewardsEditor rewards={rewards} onClose={() => setMode('pick')} onSaved={async () => { await loadRewards(); setMode('pick') }} />
  }

  return (
    <Modal title="Note eintragen" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Prämie für {childName} als Bonus gutschreiben.</p>
        <div className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--color-muted)' }}>Note</span>
          <div className="flex gap-2">
            {grades.map((g) => (
              <button key={g} type="button" onClick={() => setGrade(g)} className="flex-1 rounded-lg px-3 py-2 text-sm font-medium" style={grade === g ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { backgroundColor: 'var(--color-surface)', color: 'var(--color-ink)', border: '1px solid var(--color-line)' }}>
                {g}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--color-muted)' }}>Art</span>
          <div className="flex gap-2">
            {KIND_ORDER.map((k) => (
              <button key={k} type="button" onClick={() => setKind(k)} className="flex-1 rounded-lg px-3 py-2 text-sm" style={kind === k ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { backgroundColor: 'var(--color-surface)', color: 'var(--color-ink)', border: '1px solid var(--color-line)' }}>
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-lg px-3 py-2 text-center" style={{ backgroundColor: 'var(--color-surface)' }}>
          <span className="text-sm" style={{ color: 'var(--color-muted)' }}>Prämie: </span>
          <span className="font-semibold" style={{ color: amount > 0 ? 'var(--color-success)' : 'var(--color-muted)' }}>{formatCents(amount)}</span>
        </div>
        {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        <div className="mt-1 flex items-center justify-between">
          <button onClick={() => setMode('edit')} className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-muted)' }}>
            <Settings2 className="size-3.5" /> Beträge anpassen
          </button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
            <Button onClick={book} disabled={busy || amount <= 0}>Buchen</Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

function GradeRewardsEditor({ rewards, onClose, onSaved }: { rewards: GradeRewardDto[]; onClose: () => void; onSaved: () => void }): React.JSX.Element {
  const [rows, setRows] = useState(() => rewards.map((r) => ({ ...r, euro: (r.amountCents / 100).toFixed(2).replace('.', ',') })))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function setEuro(grade: number, kind: GradeRewardKind, euro: string): void {
    setRows((prev) => prev.map((r) => (r.grade === grade && r.kind === kind ? { ...r, euro } : r)))
  }

  async function save(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const payload: GradeRewardDto[] = rows.map((r) => ({ grade: r.grade, kind: r.kind, amountCents: parseEuroToCents(r.euro) }))
      await api.updateGradeRewards(payload)
      onSaved()
    } catch {
      setError('Bitte gültige Beträge eingeben.')
      setBusy(false)
    }
  }

  const grades = [...new Set(rows.map((r) => r.grade))].sort((a, b) => a - b)

  return (
    <Modal title="Noten-Prämien anpassen" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-[auto_1fr_1fr] items-center gap-2 text-xs" style={{ color: 'var(--color-muted)' }}>
          <span>Note</span>{KIND_ORDER.map((k) => (<span key={k}>{KIND_LABEL[k]} (€)</span>))}
        </div>
        {grades.map((g) => (
          <div key={g} className="grid grid-cols-[auto_1fr_1fr] items-center gap-2">
            <span className="w-6 text-center font-medium" style={{ color: 'var(--color-ink)' }}>{g}</span>
            {KIND_ORDER.map((k) => {
              const row = rows.find((r) => r.grade === g && r.kind === k)
              return (
                <input key={k} className={field} style={fieldStyle} inputMode="decimal" value={row?.euro ?? ''} onChange={(e) => setEuro(g, k, e.target.value)} />
              )
            })}
          </div>
        ))}
        {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        <div className="mt-1 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Zurück</Button>
          <Button onClick={save} disabled={busy}>Speichern</Button>
        </div>
      </div>
    </Modal>
  )
}

function GoalDialog({ accountId, onClose, onSaved }: { accountId: string; onClose: () => void; onSaved: () => void }): React.JSX.Element {
  const [title, setTitle] = useState('')
  const [target, setTarget] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function save(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await api.createGoal(accountId, { title: title.trim(), targetCents: parseEuroToCents(target) })
      onSaved()
    } catch {
      setError('Bitte Titel und gültigen Zielbetrag angeben.')
      setBusy(false)
    }
  }
  return (
    <Modal title="Neues Sparziel" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <input className={field} style={fieldStyle} placeholder="Titel (z. B. Skateboard)" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--color-muted)' }}>Zielbetrag (€)</span>
          <input className={field} style={fieldStyle} inputMode="decimal" placeholder="z. B. 80,00" value={target} onChange={(e) => setTarget(e.target.value)} />
        </label>
        {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        <div className="mt-1 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={save} disabled={busy || !title.trim() || !target.trim()}>Anlegen</Button>
        </div>
      </div>
    </Modal>
  )
}
